import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Check, ShieldCheck } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Field, Input } from "@/components/Field";

const Row = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };
  return (
    <div>
      <span className="block text-sm font-medium mb-1.5">{label}</span>
      <div className="flex items-center gap-2">
        <code className="flex-1 min-w-0 truncate rounded-md border border-input bg-secondary px-3 py-2.5 text-sm" data-testid={`gauth-${label}`}>{value || "—"}</code>
        <button type="button" onClick={copy} className="btn-ghost !py-2 !px-3 text-sm shrink-0" data-testid="gauth-copy">
          {copied ? <Check size={16} className="text-primary" /> : <Copy size={16} />}
        </button>
      </div>
    </div>
  );
};

export default function GoogleOAuthSection() {
  const { lang } = useI18n();
  const de = lang === "de";
  const [cfg, setCfg] = useState(null);
  const [clientId, setClientId] = useState("");
  const [secret, setSecret] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadCfg = () => {
    api.get("/admin/google-oauth").then((r) => {
      setCfg(r.data);
      setClientId(r.data.client_id || "");
      setEnabled(!!r.data.enabled);
      setSecret("");
    }).catch(() => setCfg({ error: true }));
  };
  useEffect(() => { loadCfg(); }, []);

  if (!cfg) return null;
  if (cfg.error) return (
    <div className="surface p-6"><h2 className="font-display font-bold text-lg">Google OAuth</h2>
      <p className="text-sm text-muted-foreground mt-2">{de ? "Nur Super-Admins können Google OAuth konfigurieren." : "Only super admins can configure Google OAuth."}</p></div>
  );

  const save = async () => {
    setBusy(true);
    try {
      const body = { client_id: clientId, enabled };
      if (secret && !secret.includes("•")) body.client_secret = secret;
      const { data } = await api.put("/admin/google-oauth", body);
      setCfg(data); setClientId(data.client_id || ""); setEnabled(!!data.enabled); setSecret("");
      toast.success(de ? "Google OAuth gespeichert" : "Google OAuth saved");
    } catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };
  const test = async () => {
    setBusy(true);
    try { const { data } = await api.post("/admin/google-oauth/test"); toast.success(data.message || "OK"); }
    catch (e) { toast.error(apiErr(e)); }
    setBusy(false);
  };

  return (
    <div className="surface p-6 space-y-4" data-testid="google-oauth-section">
      <div className="flex items-center justify-between gap-3">
        <h2 className="font-display font-bold text-lg flex items-center gap-2"><ShieldCheck size={18} className="text-primary" /> Google OAuth</h2>
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${cfg.configured && cfg.enabled ? "bg-primary/15 text-primary" : "bg-secondary text-muted-foreground"}`} data-testid="gauth-status">
          {cfg.configured ? (cfg.enabled ? (de ? "Aktiv" : "Active") : (de ? "Konfiguriert (deaktiviert)" : "Configured (disabled)")) : (de ? "Nicht konfiguriert" : "Not configured")}
        </span>
      </div>

      <p className="text-sm text-muted-foreground">
        {de ? "Fügen Sie diese Werte in der Google Cloud Console (APIs & Services → Anmeldedaten → OAuth-Client) hinzu:"
            : "Add these values in Google Cloud Console (APIs & Services → Credentials → OAuth client):"}
      </p>
      <Row label={de ? "Autorisierter JavaScript-Ursprung" : "Authorized JavaScript Origin"} value={cfg.js_origin} />
      <Row label={de ? "Autorisierter Weiterleitungs-URI" : "Authorized Redirect URI"} value={cfg.redirect_uri} />

      <div className="border-t border-border pt-4 space-y-4">
        <Field label="Google Client ID">
          <Input testid="gauth-client-id" value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="…apps.googleusercontent.com" />
        </Field>
        <Field label="Google Client Secret">
          <Input testid="gauth-client-secret" type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
                 placeholder={cfg.client_secret_set ? (cfg.client_secret_masked || "••••••••") : (de ? "Client Secret eingeben" : "Enter client secret")} />
          {cfg.client_secret_set && <span className="text-xs text-muted-foreground mt-1 block">{de ? "Gespeichert. Leer lassen, um es beizubehalten." : "Saved. Leave empty to keep it."}</span>}
        </Field>
        <label className="flex items-center gap-3 cursor-pointer select-none" data-testid="gauth-enable-toggle" onClick={() => setEnabled(!enabled)}>
          <span className={`relative h-6 w-11 rounded-full transition-colors ${enabled ? "bg-primary" : "bg-secondary border border-border"}`}>
            <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${enabled ? "left-[22px]" : "left-0.5"}`} />
          </span>
          <span className="text-sm font-medium">{de ? "Google-Anmeldung aktivieren" : "Enable Google login"}</span>
        </label>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={save} disabled={busy} className="btn-primary !py-2 !px-4 text-sm" data-testid="gauth-save">{de ? "Speichern" : "Save"}</button>
          <button type="button" onClick={test} disabled={busy} className="btn-ghost !py-2 !px-4 text-sm" data-testid="gauth-test">{de ? "Konfiguration testen" : "Test configuration"}</button>
        </div>
      </div>
    </div>
  );
}
